import { useHandler } from "@redotech/react-util/hook";
import LightbulbIcon from "@redotech/redo-icons/lightbulb-02.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode } from "react";
import { OptimizationProps } from "../optimization-types";
import * as styles from "./generic-settings-optimization-banner.module.css";

export interface SettingToOptimize {
  title: string;
  description: string;
  control: ReactNode;
  onUpdate: () => Promise<void>;
}

interface GenericSettingsOptimizationBannerProps extends OptimizationProps {
  settings: SettingToOptimize[];
  onApplyAll?: () => Promise<void>;
  showGenericIntro?: boolean;
}

export const GenericSettingsOptimizationBanner = memo(
  function GenericSettingsOptimizationBanner({
    settings,
    showGenericIntro = true,
    onApplyAll,
    handleComplete,
    handleDismiss,
  }: GenericSettingsOptimizationBannerProps) {
    const handleApplyAll = useHandler(async () => {
      try {
        await onApplyAll?.();
        await handleComplete();
      } catch (error) {
        console.error("Failed to apply all settings:", error);
      }
    });

    return (
      <Flex className={styles.banner} dir="column">
        <Flex align="center" justify="space-between">
          {showGenericIntro && (
            <Flex align="center" gap="sm">
              <LightbulbIcon className={styles.titleIcon} />
              <Text fontSize="md" fontWeight="semibold">
                Optimize your AI settings
              </Text>
            </Flex>
          )}
          <RedoButton
            hierarchy="tertiary"
            IconLeading={XCloseIcon}
            onClick={handleDismiss}
          />
        </Flex>

        {showGenericIntro && (
          <Text color="tertiary" fontSize="sm">
            We've analyzed your support conversations and have recommendations
            to improve your AI assistant's performance.
          </Text>
        )}

        <Flex dir="column" gap="md">
          {settings.map((setting, index) => (
            <Flex
              align="center"
              className={styles.settingRow}
              justify="space-between"
              key={index}
            >
              <Flex dir="column" gap="xs" style={{ flex: 1 }}>
                <Text fontSize="sm" fontWeight="medium">
                  {setting.title}
                </Text>
                <Text color="tertiary" fontSize="xs">
                  {setting.description}
                </Text>
              </Flex>
              <Flex align="center" gap="sm">
                {setting.control}
              </Flex>
            </Flex>
          ))}
        </Flex>

        <Flex gap="sm" mt="md">
          <RedoButton
            hierarchy="primary"
            onClick={handleApplyAll}
            size="sm"
            text="Apply all recommendations"
          />
        </Flex>
      </Flex>
    );
  },
);
